/**
 * networkDiagnostics: explains "Starbase unreachable" failures from the client.
 *
 * Since 5.51.3 we have seen a recurring signature in production: every request
 * to starbase.glamsquad.com fails within milliseconds (`Failed to fetch` /
 * `Load failed`) while requests to every other host on the page succeed. The
 * WAF, DNS, TLS and URL length have all been ruled out server-side, and a
 * blocked/unreachable request looks identical to a network outage from inside
 * the browser. So when the app has exhausted its retries it runs a short set of
 * probes and reports what it found — the one place the answer can be observed.
 *
 * The probes (each with a short timeout, all in parallel):
 *   - starbase  : GET {STARBASE_URL}/ping  — is the API host reachable at all?
 *   - origin    : GET {location.origin}/robots.txt — is *our* site reachable?
 *   - reference : GET https://www.google.com/generate_204 — is the network up?
 * plus navigator.onLine / navigator.connection and the resource-timing entries
 * for the Starbase requests that failed (duration and transfer size tell a DNS
 * or connection failure from an HTTP error, even cross-origin).
 *
 * Probes use `mode: 'no-cors'`: an opaque response still resolves, so
 * reachability is measured without needing CORS on /ping or /robots.txt.
 *
 * Reported once per document to Segment (`Starbase Unreachable`) and Bugsnag
 * (warning, "Diagnostics" tab) so it can be joined to the error page /
 * Booking Failed event it explains.
 */
import { segmentEvent } from './tracking'
import { notifyBugsnag } from './bugsnag'

const STARBASE_URL = process.env.NEXT_PUBLIC_STARBASE_URL || ''
const PROBE_TIMEOUT_MS = 3000
/** resource-timing entries faster than this with no bytes/status look like connection failures */
const FAILED_LIKE_MAX_MS = 500

/** matches the browser wordings for a failed request (see isTransientLoadError) */
export const NETWORK_FAILURE_MESSAGE = /Failed to fetch|Load failed|Network ?Error|NetworkError|ECONNRESET|timeout/i

let reportedForDocument = null

/**
 * probe: fetch a URL with a timeout and classify the outcome.
 *
 * @param {string} url
 * @returns {Promise<{outcome: 'ok'|'failed'|'timeout', ms: number, error?: string}>}
 */
export const probe = async url => {
  const started = performance.now()
  const controller = typeof AbortController === 'function' ? new AbortController() : null
  const timer = controller && setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)
  try {
    await fetch(url, { mode: 'no-cors', cache: 'no-store', credentials: 'omit', signal: controller?.signal })
    return { outcome: 'ok', ms: Math.round(performance.now() - started) }
  } catch (error) {
    const timedOut = error?.name === 'AbortError'
    return {
      outcome: timedOut ? 'timeout' : 'failed',
      ms: Math.round(performance.now() - started),
      error: timedOut ? undefined : String(error?.message || error).slice(0, 120)
    }
  } finally {
    if (timer) {
      clearTimeout(timer)
    }
  }
}

/**
 * starbaseResourceTimings: resource-timing entries for requests to the Starbase
 * host, summarized. Cross-origin entries without Timing-Allow-Origin still
 * expose duration and transferSize; a failed connection shows as a short
 * duration with zero bytes, an HTTP error as a normal duration with bytes.
 *
 * `failedLike` counts entries with no bytes, no status AND a short duration —
 * opaque cross-origin responses also report zero bytes, so bytes alone is not
 * a failure signal.
 *
 * @returns {{count:number, failedLike:number, sample:Array<{ms:number, bytes:number, status?:number}>}}
 */
export const starbaseResourceTimings = () => {
  try {
    const host = new URL(STARBASE_URL).host
    const entries = performance
      .getEntriesByType('resource')
      .filter(e => e.name.includes(host))
      .slice(-12)
    const sample = entries.map(e => ({
      ms: Math.round(e.duration),
      bytes: e.transferSize || 0,
      ...(typeof e.responseStatus === 'number' && e.responseStatus > 0 ? { status: e.responseStatus } : {})
    }))
    // transferSize is 0 for any opaque cross-origin response, so zero bytes
    // alone proves nothing; a connection failure is zero bytes AND fast AND no
    // status. Anything slower had a server talking to it.
    const failedLike = sample.filter(s => s.bytes === 0 && !s.status && s.ms < FAILED_LIKE_MAX_MS).length
    return { count: entries.length, failedLike, sample }
  } catch {
    return { count: 0, failedLike: 0, sample: [] }
  }
}

/**
 * connectionInfo: what the browser knows about the network right now.
 */
export const connectionInfo = () => {
  const c = typeof navigator !== 'undefined' ? navigator.connection : undefined
  return {
    onLine: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
    effectiveType: c?.effectiveType,
    downlink: c?.downlink,
    rtt: c?.rtt,
    saveData: c?.saveData
  }
}

/**
 * reportStarbaseUnreachable: run the probes and report the diagnostics.
 *
 * Fire-and-forget and never throws. Once per document: the first exhausted
 * request explains the session; later ones would just repeat it.
 *
 * @param {object} details
 * @param {string} details.context - where the failure surfaced ('book-catalog' | 'booking' | ...)
 * @param {Error|string} [details.error] - the failing request's error
 * @param {boolean} [details.force] - report even if this document already did (tests)
 * @returns {Promise<object|null>} the diagnostics sent, or null if skipped
 */
export const reportStarbaseUnreachable = async ({ context, error, force = false } = {}) => {
  try {
    if (typeof window === 'undefined' || typeof fetch !== 'function') {
      return null
    }
    const documentKey = performance.timeOrigin
    if (!force && reportedForDocument === documentKey) {
      return null
    }
    reportedForDocument = documentKey

    // Without a configured API host there is nothing meaningful to probe; a
    // relative '/ping' would hit the app origin and fake a verdict.
    const starbaseConfigured = /^https?:\/\//.test(STARBASE_URL)
    const [starbase, origin, reference] = await Promise.all([
      starbaseConfigured ? probe(`${STARBASE_URL}/ping`) : Promise.resolve({ outcome: 'unconfigured', ms: 0 }),
      probe(`${window.location.origin}/robots.txt`),
      probe('https://www.google.com/generate_204')
    ])

    const diagnostics = {
      context,
      errorMessage: String(error?.message || error || '').slice(0, 160),
      starbasePing: starbase.outcome,
      starbasePingMs: starbase.ms,
      starbasePingError: starbase.error,
      originProbe: origin.outcome,
      originProbeMs: origin.ms,
      referenceProbe: reference.outcome,
      referenceProbeMs: reference.ms,
      ...connectionInfo(),
      starbaseRequests: starbaseResourceTimings(),
      // the headline: starbase down for everyone (origin/reference also fail),
      // starbase-only (others fine), or already recovered by the time we probed
      verdict: !starbaseConfigured
        ? 'unknown'
        : starbase.outcome === 'ok'
          ? 'recovered'
          : origin.outcome !== 'ok' || reference.outcome !== 'ok'
            ? 'offline'
            : 'starbase-only'
    }

    segmentEvent('Starbase Unreachable', {
      ...diagnostics,
      starbaseRequests: diagnostics.starbaseRequests.count,
      starbaseRequestsFailedLike: diagnostics.starbaseRequests.failedLike
    })
    notifyBugsnag(new Error(`Starbase unreachable (${diagnostics.verdict}) during ${context}`), diagnostics, 'Diagnostics')
    return diagnostics
  } catch (reportError) {
    console.warn('reportStarbaseUnreachable:', reportError)
    return null
  }
}
